import React from 'react';
import { useTranslation } from 'react-i18next';

import { AlertTriangle, X } from 'lucide-react';

import { greyP5 } from '../../theme';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  itemName: string;
  itemType: 'manufacturer' | 'series' | 'product' | 'variant';
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getWarningMessage = () => {
    switch (itemType) {
      case 'manufacturer':
        return t('catalogue.deleteManufacturerWarning');
      case 'series':
        return t('catalogue.deleteSeriesWarning');
      case 'product':
        return t('catalogue.deleteProductWarning');
      case 'variant':
        return t('catalogue.deleteVariantWarning');
      default:
        return t('catalogue.deleteWarning');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        className="w-full max-w-md rounded-lg p-6"
        style={{ backgroundColor: greyP5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-greyP3 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-start gap-3 mb-6">
          <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white mb-2">{t('catalogue.deleteConfirmation', { name: itemName })}</p>
            <p className="text-greyP3 text-sm">{getWarningMessage()}</p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-full text-white text-sm hover:bg-greyP4 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-6 py-2 rounded-full bg-red-600 text-white text-sm font-medium
              hover:bg-red-700 transition-colors"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
